<script setup>
import { computed, ref, onMounted } from 'vue';
import { useAuthStore } from '../../stores/auth.ts';
import { useCompanyData } from '../../composables/useCompanyData.ts';
import UserDropdown from '../shared/UserDropdown.vue';

const authStore = useAuthStore();
const user = computed(() => authStore.user);
const { companyData, fetchCompanyData } = useCompanyData();

// Role-based permissions
const userRole = computed(() => user.value?.company?.role || '');
const isEmployee = computed(() => userRole.value === 'employee');
const canViewCompany = computed(() => !isEmployee.value);

onMounted(() => {
  if (user.value?.company_id) {
    fetchCompanyData();
  }
});

const blogDropdownOpen = ref(false);

const closeMenu = () => {
  blogDropdownOpen.value = false;
};

const toggleBlogDropdown = (event) => {
  event.preventDefault();
  blogDropdownOpen.value = !blogDropdownOpen.value;
};

const blogPosts = [
  { path: '/blog/the-new-workplace-rules-what-employees-need-to-know-in-2027', title: 'The New Workplace Rules: What Employees Need to Know in 2027' },
  { path: '/blog/using-ai-at-work-whats-helpful-whats-risky-and-what-to-avoid', title: 'Using AI at Work: What’s Helpful, What’s Risky, and What to Avoid' },
  { path: '/blog/ten-practical-tips-for-a-stress-free-first-day-at-a-new-job', title: '10 Practical Tips for a Stress-Free First Day at a New Job' },
  { path: '/blog/the-ultimate-onboarding-checklist-for-employers', title: 'The Ultimate Onboarding Checklist for Employers' },
  { path: '/blog/how-to-make-a-great-first-impression', title: 'How to Make a Great First Impression' },
  { path: '/blog/how-to-build-strong-relationships-with-your-new-colleagues', title: 'How to Build Strong Relationships with Your New Colleagues' },
  { path: '/blog/from-new-hire-to-high-performer-how-to-stand-out-in-your-first-year', title: 'From New Hire to High Performer: How to Stand Out in Your First Year' },
  { path: '/blog/navigating-office-politics-a-guide-for-new-employees', title: 'Navigating Office Politics: A Guide for New Employees' },
  { path: '/blog/time-management-hacks-for-new-employees', title: 'Time Management Hacks for New Employees' },
  { path: '/blog/skills-every-new-employee-should-develop-this-year', title: 'Skills Every New Employee Should Develop This Year' },
];
</script>

<template>
  <nav class="dashboard-navbar">
    <div class="dashboard-navbar-content">
      <div class="dashboard-navbar-left">
        <div class="dashboard-navbar-brand">
          <router-link to="/dashboard" class="logo-link">
            <img src="@/assets/images/logo-integrate-me.png" alt="IntegrateMe Logo" class="logo" />
          </router-link>
          <div v-if="companyData?.logo_url" class="company-brand mobile--hidden">
            <div class="brand-divider"></div>
            <img :src="companyData.logo_url" :alt="companyData.name + ' logo'" class="company-logo" />
          </div>
        </div>

        <ul class="dashboard-nav-links">
          <li class="mobile--hidden"><router-link to="/dashboard">Dashboard</router-link></li>
          <li class="mobile--hidden"><router-link to="/account/overview">Account</router-link></li>
          <li v-if="canViewCompany" class="mobile--hidden"><router-link to="/company">Company</router-link></li>
          <li>
            <div class="navbar-item has-dropdown relative">
              <a
                href="#"
                class="navbar-item transition-colors duration-200 flex items-center"
                :class="{ 'text-primary-600 font-semibold': blogDropdownOpen }"
                @click="toggleBlogDropdown"
              >
                Blog
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="dropdown-icon"
                  :class="{ 'dropdown-icon-open': blogDropdownOpen }"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  width="24"
                  height="24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </a>
              <div
                class="navbar-dropdown"
                :class="{ 'block': blogDropdownOpen }"
              >
                <router-link
                  v-for="subItem in blogPosts"
                  :key="subItem.path"
                  :to="subItem.path"
                  class="navbar-item transition-colors duration-200"
                  @click="closeMenu"
                >
                  {{ subItem.title }}
                </router-link>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div class="dashboard-navbar-right">
        <UserDropdown
          v-if="user"
          :show-dashboard="false"
        />
      </div>
    </div>
  </nav>
</template>

<style scoped>
.dashboard-navbar {
  background-color: var(--white);
  border-bottom: 1px solid var(--gray-200);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  height: 70px;
  display: flex;
  align-items: center;
}

.dashboard-navbar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 0 2rem;
  max-width: 1800px;
  margin: 0 auto;
}

.dashboard-navbar-left {
  display: flex;
  align-items: center;
}

.dashboard-navbar-brand {
  display: flex;
  align-items: center;
  height: 100%;
}

.logo {
  height: 40px;
  transition: height 0.3s ease;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.company-brand {
  display: flex;
  align-items: center;
  margin-left: 0.75rem;
}

.brand-divider {
  width: 1px;
  height: 30px;
  background-color: var(--gray-300);
  margin-right: 0.75rem;
}

.company-logo {
  height: 32px;
  max-width: 120px;
  object-fit: contain;
}

.dashboard-nav-links {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  height: 100%;
}

.dashboard-nav-links li {
  height: 100%;
  display: flex;
  align-items: center;
  margin: 0 0.75rem;
}

.dashboard-nav-links a {
  color: var(--gray-700);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.dashboard-nav-links a:hover {
  color: var(--primary);
  background-color: var(--gray-50);
}

.dashboard-nav-links a.router-link-active {
  color: var(--primary);
  font-weight: 600;
}

.dashboard-navbar-right {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.user-menu {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 6px;
  position: relative;
  transition: background-color 0.2s ease;
}

.user-menu:hover {
  background-color: var(--gray-50);
}

.user-avatar {
  width: 32px;
  height: 32px;
  background-color: var(--primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.875rem;
}

.user-name {
  font-size: 0.875rem;
  color: var(--gray-800);
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 220px;
  overflow: hidden;
  margin-top: 0.5rem;
  border: 1px solid var(--gray-200);
  z-index: 10;
}

.dropdown-header {
  padding: 1rem;
  border-bottom: 1px solid var(--gray-200);
}

.dropdown-header .email {
  font-size: 0.85rem;
  color: var(--gray-600);
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-items {
  padding: 0.5rem 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: var(--gray-700);
  font-size: 0.9rem;
  text-decoration: none;
  transition: background-color 0.2s ease;
  cursor: pointer;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
}

.dropdown-item:hover {
  background-color: var(--gray-50);
  color: var(--primary);
}

.dropdown-item i {
  width: 16px;
  text-align: center;
  color: var(--gray-500);
}

.dropdown-item:hover i {
  color: var(--primary);
}

.dropdown-divider {
  height: 1px;
  background-color: var(--gray-200);
  margin: 0.5rem 0;
}

.logout-button {
  color: var(--error);
  font-weight: 500;
}

.logout-button:hover {
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.logout-button i {
  color: var(--error);
}

.dropdown-icon {
  height: 16px;
  width: 16px;
  margin-left: 4px;
  transition: transform 0.2s ease;
}

.dropdown-icon-open {
  transform: rotate(180deg);
}

.navbar-dropdown {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 310px;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 70px - 1.5rem);
  overflow-y: auto;
  background: var(--white);
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0;
  z-index: 1000;
  border: 1px solid var(--border);
}

/* Blog dropdown specific positioning */
.navbar-item.has-dropdown .navbar-dropdown {
  left: 0;
  right: auto;
}

.navbar-dropdown.block {
  display: block;
}

.navbar-dropdown .navbar-item {
  padding: 0.5rem 1.5rem;
  margin: 0;
  border-radius: 0;
  display: flex;
  align-items: center;
  width: 100%;
}

.navbar-dropdown .navbar-item .icon {
  margin-right: 0.75rem;
  width: 1.25rem;
  text-align: center;
}

@media (max-width: 768px) {
  .dashboard-navbar-content {
    padding: 0 1rem;
  }

  .user-name {
    display: none;
  }

  .mobile--hidden {
    display: none !important;
  }

  /* Fix blog dropdown positioning on mobile */
  .navbar-item.has-dropdown .navbar-dropdown {
    right: 0;
    left: auto;
  }
  .dashboard-navbar-left {
    width: 100%;
    justify-content: space-between;
  }
}

</style>
